import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import {
  useGetKnowledgeBase,
  useGetSecurityCode,
  useSetFaqs,
  useSetFaqsFiles,
  useTwinGenerate,
  useUploadMedia,
  useValidateConsent,
} from '../../../../hooks/apiHooks/twinHooks';
import {
  useTwinData,
  useTwinErrors,
} from '../../../../hooks/storeHooks/useTwin';
import { useMatchImages } from '../../hooks/useMatchImages';
import { Heading } from '../Heading';
import { VideoRecorder } from '../VideoRecorder/VideoRecorder';
import { ValidationUi } from './ValidationUi';

export interface ConsentTabProps {
  openNextTab: () => void;
}

const RECORDING_TIME = 45;

export type ValidationStatus =
  | 'idle'
  | 'uploading'
  | 'validating'
  | 'validated'
  | 'failed';

const ConsentTab: React.FC<ConsentTabProps> = memo(({ openNextTab }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { mutateAsync: setFaqsAPI } = useSetFaqs();
  const { mutateAsync: setFaqsFilesAPI } = useSetFaqsFiles();

  const [showSpinner, setShowSpinner] = useState(false);

  const {
    previewUrl,
    audioUrl,
    jobTitle,
    firstName,
    lastName,
    knowledge,
    profileJson,
    interviewSessionId,
    faqs,
    uploadedKnowledge,
    setTwinId,
  } = useTwinData();

  const { setTabError, clearTabError } = useTwinErrors();

  const { mutateAsync: twinGenerate } = useTwinGenerate();
  const { mutateAsync: fetchKnowledgeBase } = useGetKnowledgeBase();

  const [isRecording, setIsRecording] = useState(false);
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>('idle');
  const [validationError, setValidationError] = useState<string>('');
  const [securityCode, setSecurityCode] = useState<string>('');

  const userName = useMemo(() => {
    return `${user?.profile.firstName as string} ${
      user?.profile.lastName as string
    }`;
  }, [user?.profile.firstName, user?.profile.lastName]);

  const { mutateAsync: validateConsentAPI } = useValidateConsent();
  const { mutateAsync: uploadMedia } = useUploadMedia();
  const { isMatched, isMatchProcessing, uploadValidationImage } =
    useMatchImages();
  const { refetch: fetchSecurityCode, refetch: reFetchSecurityCode } =
    useGetSecurityCode();

  const isMatchedRef = useRef(isMatched);

  useEffect(() => {
    isMatchedRef.current = isMatched;
  }, [isMatched]);

  const handleVideoRecorded = async (blob: Blob) => {
    // check: ensure we have a valid recording
    if (!blob || blob.size === 0) {
      console.error('Invalid recording: blob is empty or null / 0 bytes');
      setValidationError(t('twin.recordingFailed'));

      setValidationStatus('failed');
      return;
    }

    if (isMatchedRef.current === false) {
      setTabError('consent', 'notMatched');
      setValidationError(t('twin.faceMatchFailed'));
      setValidationStatus('failed');
      return;
    }

    console.log(`Processing recording: ${blob.size} bytes`);
    setValidationStatus('uploading');
    setValidationError('');

    try {
      // Upload the video blob to get a URL
      const videoUrl = await uploadMedia({
        file: blob,
        type: 'video',
        name: `consent-${userName}-${Date.now()}.mp4`,
      });

      if (!videoUrl) {
        throw new Error('Failed to upload video');
      }

      // Now start validation
      setValidationStatus('validating');

      // Validate the consent with the video URL
      const result = await validateConsentAPI({ videoConsentUrl: videoUrl });

      if (result.success && result.isValid) {
        setValidationStatus('validated');
        clearTabError('consent');
      } else {
        // show the error as-is from the API for consent validation, handle both success: false and success: true with isValid: false
        const apiErrorMessage =
          result.error ||
          result.message ||
          "The passcode didn't match. Double-check and re-record.";
        setValidationError(apiErrorMessage);
        setValidationStatus('failed');
      }
    } catch (error) {
      console.error('Validation failed:', error);
      // show the error as-is from the API for consent validation
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to validate consent. Please try again.';
      setValidationError(errorMessage);
      setValidationStatus('failed');
    }
  };

  const handleStartRecording = useCallback(async () => {
    const result = await fetchSecurityCode();
    if (result.data) {
      setSecurityCode(result.data.data);
    }
    setIsRecording(true);
  }, [fetchSecurityCode]);

  const handleStopRecording = () => {
    setIsRecording(false);
    setValidationStatus('uploading');
  };

  const handleFinishGeneration = async () => {
    setShowSpinner(true);

    try {
      const { id } = await twinGenerate({
        jobTitle: jobTitle || (user?.profile.jobTitle as string) || '',
        title: knowledge || '',
        firstName: firstName || (user?.profile.firstName as string) || '',
        lastName: lastName || (user?.profile.lastName as string) || '',
        voiceUrl: audioUrl || '',
        profilePictureUrl: previewUrl || '',
        knowledge: knowledge || '',
        linkedinProfile: profileJson || '',
        avatarSessionId: interviewSessionId || '',
      });
      setTwinId(id);

      const promises: Promise<unknown>[] = [];

      const nonEmptyFaqs = faqs.filter(
        (faq) => faq.question.trim() !== '' || faq.answer.trim() !== '',
      );
      if (nonEmptyFaqs.length > 0) {
        promises.push(setFaqsAPI({ faqs: nonEmptyFaqs, mentorId: id }));
      }

      const files = Object.values(uploadedKnowledge ?? {});
      const uploadUrls = files
        .filter((f) => f.status === 'uploaded' && !!f.uploadUrl)
        .map((f) => ({ url: f.uploadUrl as string, id: f.id }));

      if (uploadUrls.length > 0) {
        promises.push(setFaqsFilesAPI({ urls: uploadUrls, mentorId: id }));
      }

      if (promises.length > 0) {
        await Promise.allSettled(promises);
      }

      openNextTab();
    } catch (error) {
      console.error('Twin generation failed:', error);
      toast.error(t('twin.generateFailed'));
    } finally {
      setShowSpinner(false);
    }
  };

  const handleReFetchSecurityCode = async () => {
    const result = await reFetchSecurityCode();
    if (result.data) {
      setSecurityCode(result.data.data);
    }
  };

  return (
    <div className="w-full min-950:w-[750px] mb-auto min-1100:mb-0">
      {isRecording ? (
        <div className="w-full aspect-video  mx-auto h-fit text-white">
          <VideoRecorder
            onVideoRecorded={handleVideoRecorded}
            onRecordingStopped={handleStopRecording}
            uploadValidationImage={uploadValidationImage}
            handleReFetchSecurityCode={handleReFetchSecurityCode}
            isRecording={isRecording}
            userName={userName}
            recordingTime={RECORDING_TIME}
            isMatchProcessing={isMatchProcessing}
            securityCode={securityCode}
          />
        </div>
      ) : (
        <>
          <Heading
            title={t('twin.consentTitle')}
            subtitle={t('twin.consentSubtitle')}
          />
          <div className="w-full mx-auto h-fit text-white flex flex-col">
            <div className="h-full relative grow flex flex-col items-center justify-center">
              <ValidationUi
                validationStatus={validationStatus}
                validationError={validationError}
                isMatched={isMatched}
                isRecording={isRecording}
                isGenerating={showSpinner}
                userName={userName}
                handleStartRecording={handleStartRecording}
                handleFinishGeneration={handleFinishGeneration}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export { ConsentTab };
